// src/pages/AdvisorDashboard.js

import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getHighlights, getIntakeHighlights } from '../lib/briefUtils'
import { getAdvisorProductLabel } from '../components/HomeLink'
import { getSlotLabel } from '../lib/slotConfig'
import { setLeadStatus } from '../lib/advisorStatus'
import { fetchMyFirmName } from '../lib/partnerLookup'
import { supabase } from '../lib/supabase'

// ── Constants ──────────────────────────────────────────────────────────────────

// Labels only. Colours live in index.css as .advisor-status-<key> classes.
const STATUS_META = {
  new:       { label: 'New' },
  contacted: { label: 'Contacted' },
  converted: { label: 'Converted' },
  not_a_fit: { label: 'Not a fit' },
}


// ── Derivation helpers ─────────────────────────────────────────────────────────

function getTaxProfile(subject)       { return subject.tp || null }
function getLatestYearProfile(subject){ return subject.yp || null }



function getDisplayLabel(subject) {
  const name = subject.display_name
  if (name && name !== 'My Profile' && name.trim()) return name
  return 'Unnamed subject'
}

function formatAssignedDate(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatusDropdown({ status, disabled, onChange }) {
  const [open, setOpen] = useState(false)
  const ref             = useRef(null)
  const statusKey       = STATUS_META[status] ? status : 'new'
  const meta            = STATUS_META[statusKey]

  useEffect(() => {
    if (!open) return
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  return (
    <div ref={ref} className="advisor-status-wrap">
      <button
        className={`advisor-status-btn advisor-status-${statusKey}${disabled ? ' advisor-status-btn-static' : ''}`}
        onClick={e => { e.stopPropagation(); if (!disabled) setOpen(o => !o) }}
      >
        <span className="advisor-status-dot" />
        {meta.label}
        {!disabled && <span className="advisor-status-chevron">▾</span>}
      </button>

      {open && (
        <div className="advisor-status-menu">
          {Object.entries(STATUS_META).map(([key, m]) => (
            <button
              key={key}
              className={`advisor-status-opt${key === status ? ' active' : ''}`}
              onClick={e => { e.stopPropagation(); onChange(key); setOpen(false) }}
            >
              <span className={`advisor-status-dot advisor-status-${key}`} />
              {m.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function ChipList({ items, muted }) {
  if (!items || items.length === 0) return <span className="advisor-empty-dash">—</span>
  return (
    <div className="advisor-chips">
      {items.map(item => <span key={item} className={`advisor-chip${muted ? ' advisor-chip-muted' : ''}`}>{item}</span>)}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function AdvisorDashboard() {
  const { user }  = useAuth()
  const navigate  = useNavigate()

  const [subjects,    setSubjects]    = useState([])
  const [loading,     setLoading]     = useState(true)
  const [partnerName, setPartnerName] = useState(null)
  const [updating,    setUpdating]    = useState(null)
  const [updateError, setUpdateError] = useState(null)
  const [loadError,   setLoadError]   = useState(null)

  const loadDashboard = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setLoadError(null)

    // partner_id is read directly (not a tax fact) only to confirm this login
    // belongs to a firm; the firm name comes from get_my_firm_name. Everything
    // that touches actual subject/tax data below goes through
    // get-advisor-pipeline, and status changes go through the
    // partner_set_lead_status RPC (see lib/advisorStatus.js).
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('partner_id')
      .eq('id', user.id)
      .single()

    if (profileError || !profile?.partner_id) {
      setLoadError('No partner firm found for your account.')
      setLoading(false)
      return
    }

    const { data: firmName } = await fetchMyFirmName()
    if (firmName) setPartnerName(firmName)

    const { data: sessionData } = await supabase.auth.getSession()
    const token = sessionData?.session?.access_token
    if (!token) {
      setLoadError('Not authenticated.')
      setLoading(false)
      return
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/get-advisor-pipeline`,
        { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } }
      )
      const body = await res.json()
      if (!res.ok) throw new Error(body.error || 'Failed to load pipeline')

      // Each row is either { kind: 'lead', is_preclaim, ... } or
      // { kind: 'referral_placeholder', ... } — decided server-side by
      // get-advisor-pipeline's own consent check. Never re-derived here.
      setSubjects(body.subjects || [])
    } catch (err) {
      setLoadError(`Failed to load subjects: ${err.message}`)
    }

    setLoading(false)
  }, [user])

  useEffect(() => { loadDashboard() }, [loadDashboard])

  async function updateStatus(subjectId, newStatus) {
    if (updating) return
    setUpdating(subjectId)
    setUpdateError(null)

    const { error: updateErr } = await setLeadStatus(subjectId, newStatus)

    if (updateErr) {
      setUpdateError(`Status update failed: ${updateErr.message}`)
      setUpdating(null)
      return
    }

    setSubjects(prev => prev.map(s => s.id === subjectId ? { ...s, lead_status: newStatus } : s))
    setUpdating(null)
  }

  const totalAssigned = subjects.length

  return (
    <div className="advisor-page">

      <div className="advisor-page-header">
        <h1 className="advisor-page-title">Client Pipeline</h1>
        <p className="u-subtext">Leads assigned to {partnerName || 'your firm'}</p>
      </div>

      {!loading && subjects.length > 0 && (
        <div className="advisor-stats">
          <div className="advisor-stat-card">
            <div className="advisor-stat-value">{totalAssigned}</div>
            <div className="advisor-stat-label">Total assigned</div>
          </div>
        </div>
      )}

      {loadError && (
        <div className="advisor-error">
          <span>{loadError}</span>
        </div>
      )}

      {updateError && (
        <div className="advisor-error">
          <span>{updateError}</span>
          <button className="advisor-error-close" onClick={() => setUpdateError(null)}>×</button>
        </div>
      )}

      <div className="advisor-table-wrap">
        {loading ? (
          <div className="advisor-loading">Loading…</div>
        ) : subjects.length === 0 ? (
          <div className="advisor-empty">
            <div className="advisor-empty-icon">📋</div>
            <div className="advisor-empty-text">No leads assigned yet.</div>
            <div className="advisor-empty-sub">Leads arrive when users sign up via your referral link.</div>
          </div>
        ) : (
          <table className="advisor-table">
            <thead>
              <tr className="advisor-thead-row">
                {[['Client',''],['Activity',''],['Highlights',''],['Status',''],['Last activity',''],['Assigned','advisor-col-assigned']].map(([h,cls]) => (
                  <th key={h} className={`advisor-th${cls ? ' '+cls : ''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {subjects.map(subject => {
                // Three real states, decided server-side by get-advisor-pipeline —
                // never re-derived or second-guessed here:
                //   isPlaceholder — claimed subject, no partner_data_share consent
                //                   on record. Real tax_subjects.id (status IS
                //                   editable, detail page IS reachable), but no
                //                   name/tax facts were ever sent to this client.
                //   isPreclaim    — no tax_subject exists yet at all. subject.id
                //                   is a partner_lead_events id, not a tax_subjects
                //                   id — never navigable, never status-editable.
                //   (else)        — full consented lead.
                const isPlaceholder = subject.kind === 'referral_placeholder'
                const isPreclaim    = subject.kind === 'lead' && subject.is_preclaim
                const isFullLead    = subject.kind === 'lead' && !subject.is_preclaim

                const tp     = getTaxProfile(subject)
                const yp     = getLatestYearProfile(subject)
                const status = subject.lead_status || 'new'
                const clickable = !isPreclaim  // placeholder rows ARE navigable — get-advisor-subject returns the same safe placeholder view

                // Activity — which product(s) this row is attached to.
                // Full leads and pre-claim leads get this straight from
                // get-advisor-pipeline's `activity` array; a placeholder
                // only ever knows the single product that triggered its
                // original referral assignment (`source_product`).
                const activityProducts = isPlaceholder
                  ? (subject.source_product ? [subject.source_product] : [])
                  : (subject.activity || [])
                const activityLabels = activityProducts.map(getAdvisorProductLabel)

                // Highlights — deterministic, product-owned, capped at 3
                // (briefUtils.getHighlights). Only ever computed for a full
                // lead: tp/yp/calc are null for both placeholder and
                // pre-claim rows, by design.
                // A client who shared an intake has no tp/yp/calc; the pipeline sends a
                // light `intake` summary instead (ticket 071).
                const highlights = isFullLead
                  ? [...getHighlights(tp, yp, subject.calc), ...getIntakeHighlights(subject.intake)].slice(0, 3)
                  : []
                const comunidadValue = tp?.comunidad_autonoma || subject.intake?.comunidad || null

                const label = isPlaceholder
                  ? 'Referral in progress'
                  : isPreclaim
                    ? (subject.display_name || 'Unknown')
                    : getDisplayLabel(subject)

                return (
                  <tr
                    key={subject.id}
                    className={`advisor-row${!clickable ? ' advisor-row-lead' : ''}${isPlaceholder ? ' advisor-row-placeholder' : ''}${!clickable ? ' advisor-row-static' : ''}`}
                    onClick={() => clickable && navigate(`/advisor/subjects/${subject.id}`)}
                  >
                    <td className="advisor-td">
                      <div className={isPlaceholder ? 'advisor-placeholder-name' : 'u-label-base'}>{label}</div>
                      {isPlaceholder ? (
                        subject.masked_email && <div className="u-name-sub">{subject.masked_email}</div>
                      ) : isPreclaim ? (
                        <div className="u-name-sub">{subject.contact_email}</div>
                      ) : comunidadValue ? (
                        <div className="u-name-sub">{getSlotLabel('comunidad_autonoma', comunidadValue)}</div>
                      ) : null}
                    </td>

                    <td className="advisor-td advisor-col-activity">
                      <ChipList items={activityLabels} muted={isPlaceholder} />
                    </td>

                    <td className="advisor-td advisor-col-highlights">
                      {isPreclaim
                        ? <span className="advisor-lead-badge">Awaiting signup</span>
                        : <ChipList items={highlights} />}
                    </td>

                    {/* Status is editable for both full leads AND referral
                        placeholders — pipeline status tracking is independent
                        of consent (see partner_consent_framework.md's
                        attribution-vs-access split). Only pre-claim leads (no
                        real tax_subjects row to update) fall back to the
                        static badge. */}
                    <td className="advisor-td" onClick={e => e.stopPropagation()}>
                      {isPreclaim
                        ? <span className="advisor-lead-badge">Lead</span>
                        : <StatusDropdown
                            status={status}
                            disabled={updating === subject.id}
                            onChange={newStatus => updateStatus(subject.id, newStatus)}
                          />}
                    </td>

                    <td className="advisor-td u-text-faint-xs">
                      {formatAssignedDate(subject.updated_at || subject.lead_assigned_at)}
                    </td>

                    <td className="advisor-td u-text-faint-xs advisor-col-assigned">
                      {formatAssignedDate(subject.lead_assigned_at)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}